import { FC, useState, useRef, MutableRefObject, ReactNode } from "react";
import { z } from "zod";
import { useForm } from "@mantine/form";
import { useQueries } from "@tanstack/react-query";
import { useSnackbar, VariantType } from "notistack";
import { useDebouncedCallback } from "use-debounce";
import { useTenantSettings } from "@/hooks/useTenantSettings";

import {
  Button,
  DropDownMenu,
  FormSection,
  Grid,
  Option,
  SelectBox,
  styled,
  TextAreaInput,
  TextInput,
} from "@monolith-forensics/monolith-ui";

import diffFormData from "@/components/Forms/utils/diffFormData";
import validateAsync from "@/components/Forms/utils/zod/validateAsync";
import resolveCustomFieldValidations from "@/components/Forms/utils/resolveCustomFieldValidations";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";
import CaseSelector from "@/components/Forms/components/Selectors/CaseSelector";
import EvidencePriorities from "@/components/Evidence/EvidencePriorities";

import EvidenceAPI from "@/api/evidence";
import { EvidenceAPI as NewEvidenceAPI, SystemQuery } from "@/Features";

import EvidenceTypesApi from "@/api/evidence-types";
import LocationsAPI from "@/api/locations";
import CustomFieldsApi from "@/api/custom-fields";

import { EvidenceFormData, EditEvidenceFormData } from "../evidence.types";

import { transformCustomAttributes } from "./utils/customAttributeUtils";
import EvidenceCustomFieldsForm from "./EvidenceCustomFieldsForm";
import { CUSTOM_FIELD_CATEGORIES } from "@/pages/Settings/CustomFieldSettings/utils/customFieldConstants";
import { CustomField } from "@/Features/CustomFields/custom-fields.types";
import { TENANT_SETTINGS } from "@/constants";
import IntakeCocForm, {
  checkIntakeFieldsDirty,
  getIntakeValidationSchema,
} from "@/Features/ChainOfCustody/Forms/IntakeCocForm";
import { SignatureFieldRef } from "@/Features/Signatures";
import { ContactSelector, EvidenceType } from "@/Features";
import {
  CameraIcon,
  FileScanIcon,
  UploadIcon,
  Wand2Icon,
  RefreshCcwIcon,
} from "lucide-react";
import {
  FormDescription,
  FormTitle,
} from "@/components/Forms/components/MonolithFormComponents";
import { safeParseJSON } from "@/utils/File-parsers/safeParseJSON";

interface EvidenceProvider {
  manufacturer: string;
}

interface DefaultEvidenceFormProps {
  formTitle?: ReactNode;
  description?: ReactNode;
  defaultFormData?: EvidenceFormData;
  buttonProps?: { cancelText?: string; submitText?: string };
  onSubmit: (
    data: EvidenceFormData,
    diffData?: EditEvidenceFormData,
    signatures?: {
      receivedBySig: SignatureFieldRef;
      receivedFromSig: SignatureFieldRef;
    }
  ) => void;
  onCancel: () => void;
  includeCaseField?: boolean;
  includeIntakeDetails?: boolean;
  includeCustomFields?: boolean;
  allowSmartPaste?: boolean;
  // Child evidence customization
  isChildEvidence?: boolean;
}

// FORM VALIDATION - This will be extended with custom field validations in the component
const baseEvidenceValidationSchema = z.object({
  case_id: z.number().nullable().optional(),
  evidence_number: z.string().nullable().optional(),
  item_type: z.string().nullable().optional(),
  manufacturer: z.string().nullable().optional(),
  model_name: z.string().nullable().optional(),
  model_number: z.string().nullable().optional(),
  serial_number: z.string().nullable().optional(),
  capacity: z.coerce.number().nullable().optional(),
  capacity_unit: z.enum(["KB", "MB", "GB", "TB"]).nullable().optional(),
  contact_id: z.union([z.string(), z.number()]).nullable().optional(),
  priority_id: z.number().nullable().optional(),
  description: z.string().nullable().optional(),
  // Chain of Custody validation
  custody_to: z.any().optional(),
  custody_from: z.string().nullable().optional(),
  coc_intake_timestamp: z.string().nullable().optional(),
  location_received: z.string().nullable().optional(),
  reason: z.string().nullable().optional(),
});

const constructCustomFieldFormValues = (
  customFields: CustomField[],
  defaultData: {
    field_id: number;
    value: string | number | string[] | null;
  }[] = [],
  options: { onlyMatched?: boolean } = {}
): Record<string, string | number | string[] | null> => {
  const formValues: Record<string, string | number | string[] | null> = {};

  for (const field of customFields) {
    if (!field.enabled) continue;

    const fieldKey = `custom_attribute_${field.field_id}`;
    const match = defaultData.find((d) => d.field_id === field.field_id);

    if (!match || match.value == null) {
      if (options.onlyMatched) continue;
      formValues[fieldKey] =
        field.editor_type === "tagBox"
          ? []
          : field.editor_type === "textBox"
          ? ""
          : null;
      continue;
    }

    if (field.editor_type === "tagBox") {
      formValues[fieldKey] = Array.isArray(match.value)
        ? match.value
        : safeParseJSON(String(match.value) || "[]");
    } else if (field.editor_type === "textBox") {
      formValues[fieldKey] = match.value ?? "";
    } else {
      formValues[fieldKey] = match.value ?? null;
    }
  }

  return formValues;
};

const isEmptyCustomFieldValue = (value: unknown): boolean => {
  if (value == null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

interface EvidenceFormFieldData {
  evidenceTypes: { evi_type: string; evi_type_id: number }[];
  evidenceProviders: { manufacturer: string }[];
  locations: { location_id: number; location_name: string }[];
  customFields: CustomField[];
}

interface DefaultEvidenceFormInnerProps extends DefaultEvidenceFormProps {
  fieldData: EvidenceFormFieldData;
}

const DefaultEvidenceForm: FC<DefaultEvidenceFormProps> = ({
  includeIntakeDetails = true,
  includeCustomFields = true,
  includeCaseField = false,
  allowSmartPaste = true,
  ...rest
}) => {
  const results = useQueries({
    queries: [
      {
        queryKey: ["evidence:types"],
        queryFn: () => EvidenceTypesApi.getEvidenceTypes(),
        enabled: true,
      },
      {
        queryKey: ["evidence:providers"],
        queryFn: () => EvidenceAPI.getEvidenceBrands(),
        enabled: true,
      },
      {
        queryKey: ["evidence:locations", { include_groups: false }],
        queryFn: () => LocationsAPI.getLocations({}, { include_groups: false }),
        enabled: includeIntakeDetails,
      },
      {
        queryKey: [
          "custom_fields",
          "list",
          { category: CUSTOM_FIELD_CATEGORIES.EVIDENCE },
        ],
        queryFn: () =>
          CustomFieldsApi.getCustomFields({
            category: CUSTOM_FIELD_CATEGORIES.EVIDENCE,
          }),
        enabled: includeCustomFields,
      },
    ],
  });

  const isDone = results.every((result, index) => {
    if (index === 2 && !includeIntakeDetails) return true;
    if (index === 3 && !includeCustomFields) return true;
    return result.isFetched;
  });

  if (!isDone) return <div>Loading...</div>;

  const fieldData: EvidenceFormFieldData = {
    evidenceTypes: results[0]?.data || [],
    evidenceProviders: results[1]?.data || [],
    locations: results[2]?.data || [],
    customFields:
      results[3]?.data?.filter?.((field: CustomField) => field.enabled === 1) ||
      [],
  };

  return (
    <DefaultEvidenceFormInner
      {...rest}
      fieldData={fieldData}
      includeIntakeDetails={includeIntakeDetails}
      includeCustomFields={includeCustomFields}
      includeCaseField={includeCaseField}
      allowSmartPaste={allowSmartPaste}
    />
  );
};

const FormMessage = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const resolveDefaultFormValues = ({
  defaultFormData,
  includeCustomFields,
  includeIntakeDetails,
  fieldData,
}: {
  defaultFormData: EvidenceFormData;
  includeCustomFields: boolean;
  includeIntakeDetails: boolean;
  fieldData: EvidenceFormFieldData;
}) => {
  const values: EvidenceFormData = {
    // Main Fields
    case_id: defaultFormData?.case_id || null,
    evidence_number: defaultFormData?.evidence_number || "",
    item_type: defaultFormData?.item_type || null,
    manufacturer: defaultFormData?.manufacturer || "",
    model_name: defaultFormData?.model_name || "",
    model_number: defaultFormData?.model_number || "",
    serial_number: defaultFormData?.serial_number || "",
    capacity: defaultFormData?.capacity || 0,
    capacity_unit: defaultFormData?.capacity_unit || "GB",
    contact_id: defaultFormData?.contact_id || null,
    linked_contact: defaultFormData?.linked_contact || null,
    priority_id: defaultFormData?.priority_id || null,
    description: defaultFormData?.description || "",

    // Custom Fields
    ...(includeCustomFields
      ? constructCustomFieldFormValues(
          fieldData.customFields,
          defaultFormData?.custom_attributes || []
        )
      : {}),

    ...(includeIntakeDetails
      ? {
          // Intake Fields
          custody_to: defaultFormData?.custody_to ?? null,
          custody_from: defaultFormData?.custody_from || null,
          coc_intake_timestamp: defaultFormData?.coc_intake_timestamp || null,
          location: defaultFormData?.location || null,
          location_received: defaultFormData?.location_received || "",
          reason: defaultFormData?.reason || "",
        }
      : {}),
  };

  return values;
};

const DefaultEvidenceFormInner: FC<DefaultEvidenceFormInnerProps> = ({
  formTitle,
  description,
  defaultFormData = {},
  buttonProps,
  onSubmit,
  onCancel,
  includeCaseField = false,
  includeIntakeDetails = true,
  includeCustomFields = true,
  allowSmartPaste = true,
  isChildEvidence = false,
  fieldData,
}) => {
  const [itemType, setItemType] = useState<number | null>(
    defaultFormData?.evi_type_id || null
  );
  const [smartPasting, setSmartPasting] = useState(false);
  const receivedBySig = useRef<SignatureFieldRef | null>(null);
  const receivedFromSig = useRef<SignatureFieldRef | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { tenantSettings } = useTenantSettings();

  const { data: systemInfo } = SystemQuery.useGetSystemInfo();

  const aiEnabled = systemInfo?.AI?.enabled ?? false;

  const initialFormValues = resolveDefaultFormValues({
    defaultFormData,
    includeCustomFields,
    includeIntakeDetails,
    fieldData,
  });

  const form = useForm<EvidenceFormData>({
    mode: "controlled",
    initialValues: initialFormValues,
  });

  // Function to get the complete validation schema including intake fields
  const getEvidenceValidationSchema = (formData: EvidenceFormData) => {
    const isIntakeDirty =
      includeIntakeDetails &&
      checkIntakeFieldsDirty(
        formData,
        isChildEvidence
          ? {
              custody_from: defaultFormData?.custody_from,
              reason: defaultFormData?.reason,
            }
          : undefined
      );

    const schemaObject: any = {
      ...baseEvidenceValidationSchema.shape,
    };

    // Add case validation if needed
    if (includeCaseField) {
      schemaObject.case_id = z
        .number({
          required_error: "Case is required",
          invalid_type_error: "Case is required",
        })
        .min(1, "Case is required");
    }

    // Add custom fields validation if needed
    if (includeCustomFields) {
      const customFieldsValidation = resolveCustomFieldValidations(
        fieldData.customFields,
        itemType || undefined
      );
      Object.assign(schemaObject, customFieldsValidation);
    }

    // Add intake validation if needed
    if (includeIntakeDetails) {
      const intakeValidation = getIntakeValidationSchema(isIntakeDirty);
      Object.assign(schemaObject, intakeValidation.shape);
    }

    return z.object(schemaObject);
  };

  // Model diff data to ensure undefined values are treated as null
  const modelDiffData = (
    diffData: EditEvidenceFormData
  ): EditEvidenceFormData => {
    const modeledDiffData: EditEvidenceFormData = {};

    // Only process fields that exist in the diff data
    Object.keys(diffData).forEach((key) => {
      const value = diffData[key];
      if (value === undefined) {
        modeledDiffData[key] = null;
      } else if (key === "custom_attributes" && Array.isArray(value)) {
        // Handle custom_attributes array
        modeledDiffData[key] = value;
      } else if (key.startsWith("custom_attribute_")) {
        // Handle individual custom field properties
        // Convert undefined or empty strings to null for custom fields
        modeledDiffData[key] =
          value === undefined || value === "" ? null : value;
      } else {
        modeledDiffData[key] = value;
      }
    });

    return modeledDiffData;
  };

  // Core logic shared between real-time and submit validation
  const checkEvidenceNumberExists = async (
    evidenceNumber: string,
    options: {
      setFieldError?: boolean;
      clearErrorOnSkip?: boolean;
      throwOnError?: boolean;
    } = {}
  ): Promise<boolean | null> => {
    const {
      setFieldError = false,
      clearErrorOnSkip = false,
      throwOnError = false,
    } = options;

    // Check if duplicate prevention is enabled
    const preventDuplicates = tenantSettings?.find(
      (setting: any) =>
        setting.setting === TENANT_SETTINGS.PREVENT_DUPLICATE_EVIDENCE
    )?.enabled;

    if (!preventDuplicates || !evidenceNumber?.trim()) {
      if (clearErrorOnSkip) {
        form.clearFieldError("evidence_number");
      }
      return null; // Skip validation
    }

    // For edit scenarios, skip validation if the evidence number hasn't changed
    if (defaultFormData?.evidence_number === evidenceNumber) {
      if (clearErrorOnSkip) {
        form.clearFieldError("evidence_number");
      }
      return null; // Skip validation
    }

    try {
      const exists = await EvidenceAPI.evidenceNumberExists({
        evidence_number: evidenceNumber,
      });

      if (exists && setFieldError) {
        form.setFieldError(
          "evidence_number",
          "Duplicate Evidence Number detected"
        );
      } else if (!exists && clearErrorOnSkip) {
        form.clearFieldError("evidence_number");
      }

      return exists;
    } catch (error) {
      console.error("Error validating evidence number:", error);

      if (clearErrorOnSkip) {
        form.clearFieldError("evidence_number");
      }

      if (throwOnError) {
        throw error;
      }

      return false; // Treat API errors as validation failure for submit
    }
  };

  const validateEvidenceNumber = async (
    formData: EvidenceFormData
  ): Promise<boolean> => {
    const result = await checkEvidenceNumberExists(
      formData.evidence_number || "",
      { setFieldError: true, throwOnError: false }
    );

    // null means validation was skipped (valid), true means duplicate found (invalid)
    return result !== true;
  };

  // Debounced validation for real-time checking as user types
  const debouncedValidateEvidenceNumber = useDebouncedCallback(
    async (evidenceNumber: string) => {
      await checkEvidenceNumberExists(evidenceNumber, {
        setFieldError: true,
        clearErrorOnSkip: true,
        throwOnError: false,
      });
    },
    500 // 500ms debounce delay
  );

  const handleSubmit = async () => {
    const formData = form.getValues();
    delete formData.linked_contact; // removed linked_contact from form data as it is not needed for API submission

    const dirtyData = form.getDirty() ?? {};

    const evidenceValidationSchema = getEvidenceValidationSchema(formData);
    const validationResult = await validateAsync(evidenceValidationSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      form.setErrors(validationResult?.errors);
      // Check if any custom field validation errors exist
      const hasCustomFieldErrors = Object.keys(validationResult.errors).some(
        (key) => key.startsWith("custom_attribute_")
      );
      const hasMainFieldErrors = Object.keys(validationResult.errors).some(
        (key) => !key.startsWith("custom_attribute_")
      );

      if (hasMainFieldErrors && hasCustomFieldErrors) {
        enqueueSnackbar(
          "Please complete all required fields: Main Fields and Custom Fields.",
          { variant: "error" as VariantType }
        );
      } else if (hasCustomFieldErrors) {
        enqueueSnackbar("Please complete all required fields: Custom Fields.", {
          variant: "error" as VariantType,
        });
      } else {
        enqueueSnackbar("Please complete all required fields.", {
          variant: "error" as VariantType,
        });
      }

      return;
    }

    // Validate evidence number for duplicates
    const isValidEvidenceNumber = await validateEvidenceNumber(formData);
    if (!isValidEvidenceNumber) {
      enqueueSnackbar("Duplicate Evidence Number detected.", {
        variant: "error" as VariantType,
      });
      return;
    }

    // use dirty fields to construct diffData for edit scenarios, ensuring undefined values are included as null
    // ensures that only fields that have been changed or updated by the user will be provided to the onSubmit handler
    const diffData = Object.keys(dirtyData).reduce((diff, key) => {
      const value = form.getValues()[key as keyof EvidenceFormData];

      if (
        key.startsWith("custom_attribute_") &&
        isEmptyCustomFieldValue(initialFormValues[key]) &&
        isEmptyCustomFieldValue(value)
      ) {
        return diff;
      }

      diff[key] = value;
      return diff;
    }, {} as EditEvidenceFormData);

    const modeledDiffData = modelDiffData(diffData);

    onSubmit?.(formData, modeledDiffData, {
      receivedBySig: receivedBySig.current as SignatureFieldRef,
      receivedFromSig: receivedFromSig.current as SignatureFieldRef,
    });
  };

  const handleSmartPaste = async () => {
    try {
      setSmartPasting(true);
      const text = await navigator.clipboard.readText();
      const result = await NewEvidenceAPI.AI.parseEvidence({ content: text });

      if (!result?.data) throw new Error("No data returned from AI parse");

      const selectedType = fieldData.evidenceTypes.find(
        (t: EvidenceType) => t.evi_type === result.data.type
      );

      const validCapacityUnits = ["KB", "MB", "GB", "TB"];
      const resolvedCapacityUnit = validCapacityUnits.includes(
        result?.data?.capacity_unit ?? ""
      )
        ? result.data.capacity_unit
        : null;

      const targetTypeId = selectedType?.evi_type_id ?? itemType ?? null;
      const applicableFields = fieldData.customFields.filter(
        (f) =>
          !f.type_ids?.length ||
          (targetTypeId && f.type_ids.includes(targetTypeId))
      );

      form.setValues({
        ...(result.data.evidence_number != null && {
          evidence_number: result.data.evidence_number,
        }),
        ...(result.data.make != null && { manufacturer: result.data.make }),
        ...(result.data.model_name != null && {
          model_name: result.data.model_name,
        }),
        ...(result.data.model_number != null && {
          model_number: result.data.model_number,
        }),
        ...(result.data.unique_id != null && {
          serial_number: result.data.unique_id,
        }),
        ...(result.data.capacity != null && { capacity: result.data.capacity }),
        ...(resolvedCapacityUnit != null && {
          capacity_unit: resolvedCapacityUnit,
        }),
        ...(selectedType && { item_type: result.data.type }),
        ...(result.data.priority_id != null && {
          priority_id: result.data.priority_id,
        }),
        ...(result.data.description != null && {
          description: result.data.description,
        }),
        ...(includeCustomFields
          ? constructCustomFieldFormValues(
              applicableFields,
              result.data.custom_fields ?? [],
              { onlyMatched: true }
            )
          : {}),
      });

      if (selectedType) {
        setItemType(selectedType.evi_type_id);
      }
      enqueueSnackbar("Evidence details scanned successfully.", {
        variant: "success" as VariantType,
      });
    } catch (error) {
      const message =
        error instanceof DOMException && error.name === "NotAllowedError"
          ? "Clipboard access denied. Please allow clipboard permissions and try again."
          : "Error parsing evidence details.";
      enqueueSnackbar(message, {
        variant: "error" as VariantType,
      });
      console.error("Error parsing evidence details:", error);
    } finally {
      setSmartPasting(false);
    }
  };

  const handleResetForm = () => {
    form.clearErrors();
    form.setValues(
      resolveDefaultFormValues({
        defaultFormData: defaultFormData || {},
        includeCustomFields,
        includeIntakeDetails,
        fieldData,
      })
    );

    setItemType(defaultFormData?.evi_type_id || null);
    setSmartPasting(false);
  };

  const handleCancel = () => {
    form.reset();
    onCancel?.();
  };

  const capacityUnits = [
    { value: "KB", label: "KB" },
    { value: "MB", label: "MB" },
    { value: "GB", label: "GB" },
    { value: "TB", label: "TB" },
  ];

  return (
    <>
      <div>
        <div
          style={{
            display: "flex",
            gap: 10,
            marginBottom: description ? 8 : 20,
            alignItems: "center",
          }}
        >
          {formTitle && (
            <FormTitle data-testid="evidence-form-title">{formTitle}</FormTitle>
          )}
          {smartPasting && (
            <FormMessage>Smart pasting in progress...</FormMessage>
          )}
          <ButtonMenu>
            <DropDownMenu
              size="xxs"
              arrow
              disabled={smartPasting}
              data={[
                // TODO: Implement file scanning feature before enabling this option
                {
                  label: "Scan File",
                  value: "scan-file",
                  leftSection: <FileScanIcon size={14} />,
                  disabled: !aiEnabled,
                  visible: false,
                },
                {
                  label: "Smart Paste",
                  value: "smart-paste",
                  leftSection: <Wand2Icon size={14} />,
                  onClick: handleSmartPaste,
                  disabled: !aiEnabled || !allowSmartPaste,
                },
                // TODO: Implement file attachment features before enabling this options
                {
                  label: "Attach File",
                  value: "attach-file",
                  leftSection: <UploadIcon size={14} />,
                  visible: false,
                },
                // TODO: Implement photo attachment features before enabling this option
                {
                  label: "Attach Photo",
                  value: "attach-photo",
                  leftSection: <CameraIcon size={14} />,
                  visible: false,
                },
                {
                  label: "Reset Form",
                  value: "reset-form",
                  leftSection: <RefreshCcwIcon size={14} color="limegreen" />,
                  onClick: handleResetForm,
                },
              ]}
              onChange={() => {}}
            >
              Options
            </DropDownMenu>
          </ButtonMenu>
        </div>
        {description && (
          <FormDescription style={{ marginBottom: 20 }}>
            {description}
          </FormDescription>
        )}
        {/* Evidence Details Section */}
        <Grid col={2}>
          {includeCaseField && (
            <CaseSelector
              size="sm"
              variant="outlined"
              label="Case"
              placeholder="Select Case..."
              description="Select the case this evidence belongs to"
              required
              searchable
              clearable
              openOnFocus={false}
              key={form.key("case_id")}
              {...form.getInputProps("case_id")}
              // data={fieldData.caseNumbers}
            />
          )}
          {includeCaseField && <div></div>}

          <TextInput
            size="sm"
            variant="outlined"
            label="Evidence Number"
            placeholder="Leave blank to auto-generate"
            description="Enter a unique ID that identifies this evidence"
            data-testid="evidence-form-number-input"
            key={form.key("evidence_number")}
            {...form.getInputProps("evidence_number")}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              // Call the original form onChange handler
              form.getInputProps("evidence_number").onChange?.(e);
              // Trigger debounced validation
              debouncedValidateEvidenceNumber(e.target.value);
            }}
          />

          {/* SelectBox doesn't forward data-testid, so the wrapper carries it */}
          <div data-testid="evidence-form-type-select">
            <SelectBox
              size="sm"
              variant="outlined"
              label="Evidence Type"
              placeholder="Select Evidence Type..."
              description="Type of evidence"
              searchable
              data={fieldData.evidenceTypes.map(
                (type: { evi_type: string; evi_type_id: number }) => ({
                  value: type.evi_type,
                  label: type.evi_type,
                })
              )}
              key={form.key("item_type")}
              {...form.getInputProps("item_type")}
              onChange={(value) => {
                form.setFieldValue("item_type", value);
                const selectedType = fieldData.evidenceTypes.find(
                  (t: EvidenceType) => t.evi_type === value
                );
                setItemType(selectedType?.evi_type_id || null);
              }}
            />
          </div>

          <SelectBox
            size="sm"
            variant="outlined"
            label="Item Brand"
            placeholder="Apple, Google, Samsung..."
            description="Brand or manufacturer of the item"
            searchable
            allowCustomValue
            data={fieldData.evidenceProviders.map(
              (provider: EvidenceProvider) => ({
                value: provider.manufacturer,
                label: provider.manufacturer,
              })
            )}
            key={form.key("manufacturer")}
            {...form.getInputProps("manufacturer")}
          />

          <TextInput
            size="sm"
            variant="outlined"
            label="Item Name"
            placeholder="iPhone Xs, WINSRV003, Smith Email Account..."
            description="Enter the model name of the device"
            data-testid="evidence-form-item-name-input"
            key={form.key("model_name")}
            {...form.getInputProps("model_name")}
          />

          <TextInput
            size="sm"
            variant="outlined"
            label="Model Number or Service"
            placeholder="A1234, WD2500, GMail..."
            description="Enter the model number for this item"
            data-testid="evidence-form-model-number-input"
            key={form.key("model_number")}
            {...form.getInputProps("model_number")}
          />

          <TextInput
            size="sm"
            variant="outlined"
            label="Unique Identifier"
            placeholder="first.last@email.com, serial number..."
            description="Enter a unique identifier for this item"
            data-testid="evidence-form-unique-id-input"
            key={form.key("serial_number")}
            {...form.getInputProps("serial_number")}
          />

          <Grid col={2}>
            <TextInput
              size="sm"
              variant="outlined"
              label="Size"
              placeholder="0"
              description="Size of file or item"
              type="number"
              min={0}
              data-testid="evidence-form-size-input"
              key={form.key("capacity")}
              {...form.getInputProps("capacity")}
            />
            <SelectBox
              size="sm"
              variant="outlined"
              label="Size Unit"
              placeholder="Select unit"
              description="Select size unit KB, MB, GB, etc"
              data={capacityUnits}
              key={form.key("capacity_unit")}
              {...form.getInputProps("capacity_unit")}
            />
          </Grid>
          <ContactSelector
            query={{ case_id: form.getValues().case_id || undefined }}
            disabled={!form.getValues().case_id}
            size="sm"
            variant="outlined"
            label="Linked Contact"
            clearable={true}
            allowCreateNew={true}
            onContactCreated={(contact) => {
              form.setFieldValue("contact_id", contact.client_id);
              form.setFieldValue("linked_contact", {
                value: contact.client_id,
                label: contact.name,
                data: contact,
              });
            }}
            key={form.key("linked_contact")}
            {...form.getInputProps("linked_contact")}
            onChange={(_, option) => {
              form.setFieldValue("linked_contact", option);
              form.setFieldValue("contact_id", option?.value);
            }}
          />

          <SelectBox
            size="sm"
            variant="outlined"
            label="Evidence Priority"
            placeholder="Select Evidence Priority..."
            description="Priority level of this evidence"
            searchable
            data={EvidencePriorities.map(({ priority_id, priority_name }) => ({
              value: priority_id,
              label: priority_name,
            }))}
            renderOption={(option: Option | string) => {
              if (typeof option === "string") {
                return <div>{option}</div>;
              }

              const priorityData = EvidencePriorities.find(
                (p) => p.priority_id === option.value
              );
              if (!priorityData) {
                return <div>{option.label}</div>;
              }

              const IconComponent = priorityData.icon;

              return (
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <IconComponent
                    size={16}
                    style={{ color: priorityData.color }}
                  />
                  <span>{priorityData.priority_name}</span>
                </div>
              );
            }}
            key={form.key("priority_id")}
            {...form.getInputProps("priority_id")}
          />
        </Grid>

        <div style={{ marginTop: "1rem" }}>
          <TextAreaInput
            size="sm"
            variant="outlined"
            label="Description"
            placeholder="Enter a brief description of this item and any important details"
            description="Brief description of this evidence item"
            minRows={5}
            data-testid="evidence-form-description-input"
            key={form.key("description")}
            {...form.getInputProps("description")}
          />
        </div>

        {/* Custom Fields Section */}
        {includeCustomFields && (
          <EvidenceCustomFieldsForm
            form={form}
            itemType={itemType}
            defaultOpen={!!defaultFormData}
          />
        )}

        {/* Chain of Custody Section */}
        {includeIntakeDetails && (
          <FormSection
            title={"Intake Details"}
            defaultOpen={false}
            style={{ marginTop: 15 }}
          >
            <IntakeCocForm
              form={form}
              receivedBySig={
                receivedBySig as MutableRefObject<SignatureFieldRef>
              }
              receivedFromSig={
                receivedFromSig as MutableRefObject<SignatureFieldRef>
              }
              isChildEvidence={isChildEvidence}
            />
          </FormSection>
        )}

        <ButtonMenu>
          <Button size="xs" variant="subtle" onClick={handleCancel}>
            {buttonProps?.cancelText || "Cancel"}
          </Button>
          <Button
            size="xs"
            color="primary"
            variant="contained"
            data-testid="evidence-form-submit-button"
            onClick={handleSubmit}
          >
            {buttonProps?.submitText || "Submit"}
          </Button>
        </ButtonMenu>
      </div>
    </>
  );
};

export default DefaultEvidenceForm;
